<template>
  <div
    v-if="!useRsTabs"
    class="d-flex flex-column h-100"
  >
    <div
      class="tab-nav"
      :class="navClasses"
    >
      <div class="tab-nav__wrap">
        <div
          v-for="(tab, index) in tabs"
          :id="`nav-tab-${id}-${tab}`"
          :key="index"
          class="tab-nav__item"
          :class="{
            'tab-nav__item--active': activeTab === tab,
          }"
          @click="($event) => onTabClick($event, tab)"
        >
          <span class="tab-nav__title"><slot :name="tabHeadSlotName(tab)">{{ tab }} </slot></span>
        </div>
        <div class="tab-nav__slider">
          <div
            class="indicator"
            :style="{ transform: `translateX(${lineStyles.left}px)`, width: `${lineStyles.width}px` }"
          />
        </div>
      </div>
      <div
        v-if="!!$slots.default"
        class="ml-auto"
      >
        <slot />
      </div>
    </div>
    <div
      v-if="scrollable ? true : false"
      class="flex-fill"
    >
      <OverlayScrollbars
        :options="{
          sizeAutoCapable: false,
        }"
        class="tab-panel"
      >
        <slot :name="tabPanelSlotName" />
      </OverlayScrollbars>
    </div>
    <div v-else>
      <slot :name="tabPanelSlotName" />
    </div>
  </div>
  <Component
    :is="'b-tabs'"
    v-else
    class="rs-tabs"
    :value="parseInt(`${(initialTab || '0')}`, 10)"
    :class="navClasses"
    no-fade
    :align="align"
    :justified="justified"
    :lazy="lazy"
    :slim="slim"
    @input="(tab) => $emit('input', tab)"
  >
    <slot />
  </Component>
</template>
<script lang="ts">
import { defineComponent } from 'vue';
import { Nullable } from '@/shared';

type lineProps = {
  left?: number,
  width?: number,
} | undefined;

type RsTab2Props = {
  activeTab?: string,
  line?: lineProps,
  lineTemp?: Nullable<lineProps>,
};

export default defineComponent({
  name: 'RsTabs2',
  props: {
    changeActiveTab: {
      type: [Number, String, Boolean],
      default: false,
    },
    initialTab: [String, Number],
    tabs: Array,
    main: {
      type: Boolean,
      default: false,
    },
    id: {
      type: String,
      default: 'default',
    },
    fixed: {
      type: Boolean,
      default: false,
    },
    scrollable: {
      type: Boolean,
      default: true,
    },
    /*
      This prop allows to either use the RsTab components instead of writing the templates by hand
    */
    useRsTabs: {
      type: Boolean,
      default: false,
    },
    /**
     * Renders the tabs with slimmer style. For use in modals and off canvas.
     */
    slim: {
      type: Boolean,
      default: false,
    },
    /**
     * Lazily render this tab's content when shown
     *
     */
    lazy: {
      type: Boolean,
      default: false,
    },
    /**
     * Fills all horizontal space with nav items, but unlike 'fill', every nav item will be the same width
     *
     */
    justified: {
      type: Boolean,
      default: false,
    },
    /**
     * Accepts: left, right, center
     *
     */
    align: {
      type: String,
    },
  },
  data() {
    return {
      activeTab: this.initialTab,
      line: {
        left: 0,
        width: 69,
      },
      lineTemp: null,
    } as RsTab2Props;
  },
  computed: {
    tabPanelSlotName():string {
      return `tab-panel-${this.id}-${this.activeTab}`;
    },
    lineStyles():lineProps {
      return (this.lineTemp == null) ? this.line : this.lineTemp;
    },
    navClasses() {
      return {
        'tab-nav--main': this.main,
        'tab-nav--fixed': this.fixed,
        'rs-tabs--slim': this.slim,
      };
    },
  },
  watch: {
    changeActiveTab() {
      this.activeTab = '1';
      this.onTabClick(null, this.activeTab);
    },
  },

  mounted(): void {
    const tempName = `nav-tab-${this.id}-${(this.initialTab || '1')}`;
    const tab = document.getElementById(tempName);
    if (tab) {
      const styles = {
        left: tab.offsetLeft,
        width: tab.clientWidth,
      };
      this.line = styles;
    }
  },
  methods: {
    tabHeadSlotName(tabName:string) {
      return `tab-nav-${this.id}-${tabName}`;
    },

    onTabClick(evt: Event | null, tabName: string) {
      this.activeTab = tabName;
      const tab = document.getElementById(`nav-tab-${this.id}-${tabName}`) as HTMLElement;
      if (!tab) return;
      const styles = {
        left: tab.offsetLeft,
        width: tab.clientWidth,
      };
      this.line = styles;
      this.$emit('on-tab-changed', tabName);
    },
  },
});
</script>
<style lang="scss">
@import '@/styles/_variables.scss';

.tab-nav{
  position: relative;
  display: flex;
  align-items: center;
  &__slider {
    position: absolute;
    bottom: 0;
    height: 3px;
    width: 100%;
    .indicator {
      position: absolute;
      left: 0;
      height: 3px;
      background-color: $secondary-color;
      border-radius: 3px 3px 0 0;
      pointer-events: none;
      transition: width 0.15s cubic-bezier(0.4, 0.0, 1, 1), transform 0.15s cubic-bezier(0.4, 0.0, 1, 1);
    }
  }
  &__item{
    position: relative;
    display: inline-flex;
    align-items: center;
    margin: 0 0.5rem;
    padding: 0;
    border: none;
    font-size: 1rem;
    color: $tertiary-muted;
    background: transparent;
    transition: color 0.2s ease;
    cursor: pointer;
    &--active{
      font-weight: 700;
      color: $secondary-color-dark;
      &:before{
        width: 100%;
        background-color: $primary-color;
      }
      &:hover{
        &:before{
          background-color: $primary-color;
        }
      }
    }
  }
  &__title{
    font-size: rem-calc(14px);
  }
  &--main{
    padding: 0 2rem;
    height: 56px;
    background-color: $white;
    border-bottom: 1px solid $tertiary-gray;
    .tab-nav__wrap{
      position: relative;
      display: flex;
      height: 100%;

      &.unselected > .slider {
        opacity: 0;
        visibility: hidden;
      }
      .tab-nav__item{
        margin: 0 2rem 0 0.5rem;
        padding: 0;
      }
    }
  }
  &--fixed{
    z-index: 99;
    right: 0;
    position: fixed;
  }
}
.tab-panel{
  height: 100%;
  overflow: auto;
}
.rs-tabs {
  .nav-tabs {
    border: none;
    .nav-item {
      margin-top: 0;
      a {
        position: relative;
        display: inline-flex;
        align-items: center;
        margin-right: 1.5rem;
        padding: 0 0 1rem 0;
        border: none;
        font-size: 1rem;
        color: rgba($dark-gray, 0.6);
        background: transparent;
        transition: color 0.2s ease;
        span {
          line-height: 1rem;
        }
        .rs-badge {
          margin-left: 0.6rem;
        }
        &:before {
          content: '';
          position: absolute;
          left: 0;
          bottom: 0;
          width: 0;
          height: 3px;
          border-radius: 3px;
          border-radius: 3px 3px 0 0;
          background-color: transparent;
          pointer-events: none;
          transition: width 0.15s cubic-bezier(0.4, 0, 1, 1),
          transform 0.15s cubic-bezier(0.4, 0, 1, 1);
        }
        &:focus {
          outline: none;
        }
        &:hover {
          color: $dark-gray;
          &:before {
            width: 100%;
          }
        }
        &.active {
          font-weight: 700;
          color: $dark-gray;
          &:before {
            width: 100%;
            background-color: $secondary-color;
          }
        }
      }
    }
  }
  .tab-content {
    margin-top: 1rem;
  }
  &--slim {
    .nav-tabs {
      border-bottom: 1px solid $tertiary-gray;
      .nav-item {
        a {
          font-size: rem-calc(14px);
          color: $tertiary-muted;
          &:before {
            height: 3px;
          }
          &.active {
            color: $secondary-color-dark;
          }
        }
      }
    }
  }
}
</style>
